import React from 'react'
import { HistoryIcon, PlusIcon, StickyNoteIcon } from 'lucide-react'
import { ActivityLog, formatActivityMeta } from '../utils/activityLogger'

export interface PanelNote {
  id?: string
  note: string
  timestamp?: string
  created_at?: string
  created_by_name?: string
}

export interface NotesAndActivityPanelProps {
  notes: PanelNote[]
  activities: ActivityLog[]
  activitiesLoading?: boolean
  notesTitle?: string
  newNote?: string
  onNewNoteChange?: (value: string) => void
  onAddNote?: () => void
  savingNote?: boolean
  readOnly?: boolean
  showAddNote?: boolean
  beforePanels?: React.ReactNode
  notesExtra?: React.ReactNode
  activityEmptyMessage?: string
  notesEmptyMessage?: string
  className?: string
  /** Side-by-side notes and activity on large screens */
  layout?: 'stacked' | 'split'
  /** Smaller typography and tighter padding for dense lists */
  compact?: boolean
}

function getNoteKey(note: PanelNote, index: number): string {
  return note.id || note.timestamp || note.created_at || `note-${index}`
}

function getNoteTimestamp(note: PanelNote): string | undefined {
  return note.timestamp || note.created_at
}

export function NotesAndActivityPanel({
  notes,
  activities,
  activitiesLoading = false,
  notesTitle = 'Notes',
  newNote = '',
  onNewNoteChange,
  onAddNote,
  savingNote = false,
  readOnly = false,
  showAddNote = true,
  beforePanels,
  notesExtra,
  activityEmptyMessage = 'No recent activity',
  notesEmptyMessage = 'No notes available',
  className = '',
  layout = 'stacked',
  compact = false,
}: NotesAndActivityPanelProps) {
  const canAddNote = showAddNote && !readOnly && onAddNote && onNewNoteChange
  const titleClass = compact ? 'text-base font-semibold' : 'text-xl font-semibold'
  const iconClass = compact ? 'w-5 h-5' : 'w-6 h-6'
  const bodyTextClass = compact ? 'text-xs text-gray-700 leading-relaxed' : 'text-sm text-gray-700'
  const metaTextClass = compact ? 'text-[11px] text-gray-500' : 'text-xs text-gray-500'
  const cardPadding = compact ? 'p-3' : 'p-4'
  const panelPadding = compact ? 'p-3' : 'p-4'

  const notesPanel = (
    <div
      className={`flex flex-col min-h-0 bg-amber-50 rounded-lg border border-amber-100 ${panelPadding} ${
        layout === 'split' ? 'h-full' : 'flex-1 min-h-[280px]'
      }`}
    >
      <div className={`flex items-center gap-2 mb-2 shrink-0 ${compact ? '' : 'mb-3'}`}>
        <StickyNoteIcon className={`${iconClass} text-amber-600 shrink-0`} />
        <h2 className={`${titleClass} text-gray-900`}>
          {notesTitle} ({notes.length})
        </h2>
      </div>

      {notesExtra && <div className="mb-2 shrink-0">{notesExtra}</div>}

      {canAddNote && (
        <div className={`mb-2 shrink-0 bg-white rounded-lg ${cardPadding} border border-amber-100 shadow-sm`}>
          <h3 className="text-xs font-medium text-gray-700 mb-2">Add Note</h3>
          <div className="flex gap-2">
            <textarea
              value={newNote}
              onChange={(e) => onNewNoteChange!(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey && newNote.trim()) {
                  onAddNote!()
                }
              }}
              rows={compact ? 2 : 3}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add a note... (Ctrl+Enter to submit)"
            />
            <button
              type="button"
              onClick={onAddNote}
              disabled={!newNote.trim() || savingNote}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-start flex items-center gap-1.5"
            >
              {savingNote ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                  Saving...
                </>
              ) : (
                <>
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-[180px] overflow-y-auto space-y-2 pr-1">
        {notes.length > 0 ? (
          notes.map((note, index) => {
            const ts = getNoteTimestamp(note)
            return (
              <div
                key={getNoteKey(note, index)}
                className={`bg-white rounded-lg border border-amber-100 ${cardPadding} shadow-sm`}
              >
                <p className={bodyTextClass}>{note.note}</p>
                <div className="flex items-center justify-between mt-1.5 gap-2">
                  {ts && <p className={metaTextClass}>{new Date(ts).toLocaleString()}</p>}
                  {note.created_by_name && (
                    <p className={metaTextClass}>By: {note.created_by_name}</p>
                  )}
                </div>
              </div>
            )
          })
        ) : (
          <div className="bg-white rounded-lg border border-dashed border-amber-200 p-6 text-center">
            <StickyNoteIcon className="w-8 h-8 text-amber-300 mx-auto mb-2" />
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500`}>{notesEmptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  )

  const activityPanel = (
    <div
      className={`flex flex-col min-h-0 bg-slate-50 rounded-lg border border-slate-200 ${panelPadding} ${
        layout === 'split' ? 'h-full' : 'flex-1 min-h-[280px]'
      }`}
    >
      <div className={`flex items-center gap-2 shrink-0 ${compact ? 'mb-2' : 'mb-3'}`}>
        <HistoryIcon className={`${iconClass} text-slate-600 shrink-0`} />
        <h2 className={`${titleClass} text-gray-900`}>Activity ({activities.length})</h2>
      </div>

      <div className="flex-1 min-h-[180px] overflow-y-auto pr-1">
        {activitiesLoading ? (
          <div className="bg-white rounded-lg border border-dashed border-slate-200 p-6 text-center">
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500`}>Loading activity...</p>
          </div>
        ) : activities.length > 0 ? (
          <div className="space-y-2">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className={`bg-white rounded-lg border border-slate-100 ${cardPadding} shadow-sm`}
              >
                <p className={bodyTextClass}>{activity.description}</p>
                <p className={`${metaTextClass} mt-1.5`}>{formatActivityMeta(activity)}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-dashed border-slate-200 p-6 text-center">
            <HistoryIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500`}>{activityEmptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 overflow-hidden gap-3 ${
        layout === 'stacked' ? 'min-h-[min(70vh,640px)]' : ''
      } ${className}`}
    >
      {beforePanels && <div className="shrink-0">{beforePanels}</div>}

      {layout === 'split' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 flex-1 min-h-[min(60vh,520px)] min-h-0">
          {notesPanel}
          {activityPanel}
        </div>
      ) : (
        <>
          {notesPanel}
          {activityPanel}
        </>
      )}
    </div>
  )
}
